/**
 * Recovery mode detection utilities for session gap handling
 */

export interface RecoveryContext {
  detectedAt: number;
  gapDuration: number;
  isRecoveryMode: boolean;
}

/**
 * Check if we're currently in recovery mode (within 30 seconds of gap detection)
 */
export const isInRecoveryMode = (): boolean => {
  const recoveryInfo = localStorage.getItem('sessionGapDetected');
  if (!recoveryInfo) return false;
  
  try {
    const info: RecoveryContext = JSON.parse(recoveryInfo);
    // Recovery mode lasts for 30 seconds after detection
    return (Date.now() - info.detectedAt) < 30000;
  } catch {
    return false;
  }
};

/**
 * Get current recovery context if available
 */
export const getRecoveryContext = (): RecoveryContext | null => {
  const recoveryInfo = localStorage.getItem('sessionGapDetected');
  if (!recoveryInfo) return null;
  
  try {
    return JSON.parse(recoveryInfo);
  } catch {
    return null;
  }
};

/**
 * Set recovery mode for session gap detection
 */
export const setRecoveryMode = (gapDuration: number): void => {
  const recoveryContext: RecoveryContext = {
    detectedAt: Date.now(),
    gapDuration,
    isRecoveryMode: true
  };
  
  localStorage.setItem('sessionGapDetected', JSON.stringify(recoveryContext));
  
  // Auto-clear after 30 seconds
  setTimeout(() => {
    clearRecoveryMode();
  }, 30000);
};

/**
 * Clear recovery mode flag
 */
export const clearRecoveryMode = (): void => {
  localStorage.removeItem('sessionGapDetected');
};

/**
 * Get last session end time for gap calculation
 */
export const getLastSessionEnd = (): number | null => {
  const endTime = localStorage.getItem('lastSessionEnd');
  return endTime ? parseInt(endTime) : null;
};

/**
 * Set last session end time
 */
export const setLastSessionEnd = (timestamp?: number): void => {
  localStorage.setItem('lastSessionEnd', (timestamp || Date.now()).toString());
};